// # Profile page component
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../auth/state';
import { doc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import Header from '../../landing/components/header';  // # Import landing Header
import { 
  MdPhotoCamera, 
  MdClose, 
  MdPerson, 
  MdArticle, 
  MdStars, 
  MdShare, 
  MdMessage, 
  MdPersonAdd, 
  MdEdit, 
  MdZoomIn,
  MdQuestionAnswer,
  MdPeople,
  MdEmojiEvents,
  MdWork,
  MdThumbUp,
  MdDescription,
  MdChevronLeft,
  MdChevronRight
} from 'react-icons/md';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { generateCertificatePDF } from '../../roles/admin/admin_tools/user_manage/usr_mng_utils/user_cert';
import ProfilePosts from './prfl_sections/prfl_posts';
import ProfileQuestions from './prfl_sections/prfl_quest';
import ProfileFriends from './prfl_sections/prfl_friends';
import ProfileAchievements from './prfl_sections/prfl_achievements';
import ProfileExperiences from './prfl_sections/prfl_experiences';
import ProfileLikes from './prfl_sections/prfl_likes';
import ProfileResume from './prfl_sections/prfl_resumee';
import ProfileAbout from './prfl_sections/prfl_about';
import ProfileHonors from './prfl_sections/prfl_honors';
import WebIdShare from './web-id'; // # Import the WebIdShare component
import './profile.css';
import './profile_ux.css';  /* # Import UX enhancements */
import cacheManager from '../../auth/cache_manager';  // # Import cache manager

const compressImage = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          resolve(new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          }));
        }, 'image/jpeg', 0.7); // 70% quality
      };
    };
  });
};

// # Utility functions
export const capitalizeFirstLetter = (string) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

const getProfilePhotoUrl = (userProfile) => {
  try {
    if (!userProfile?.photoURL) return '/icons/pfp.png';
    if (userProfile.photoURL === '') return '/icons/pfp.png';
    
    // Check if it's a full URL (from Storage)
    if (userProfile.photoURL.startsWith('http')) {
      return userProfile.photoURL;
    }
    
    // If it's just a filename, assume it's the default
    return '/icons/pfp.png';
  } catch (error) {
    console.error('Error getting profile photo:', error);
    return '/icons/pfp.png';
  }
};

// # Update the date formatting function
const formatJoinDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Date not available';
  }
};

const Profile = ({ viewUsername, isPublicView }) => {
  // # State management
  const { user, userProfile, isAdmin } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingCover, setSavingCover] = useState(false);
  const [activeTab, setActiveTab] = useState('about');
  const [showCoverCarousel, setShowCoverCarousel] = useState(false);
  const [currentCover, setCurrentCover] = useState(userProfile?.cover || 'cover1.jpg');
  const [saveMessage, setSaveMessage] = useState('');
  const [showPhotoCarousel, setShowPhotoCarousel] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [photoMessage, setPhotoMessage] = useState('');
  // # State for viewed user when viewing another user's profile
  const [viewedUserProfile, setViewedUserProfile] = useState(null);
  const [viewedUserError, setViewedUserError] = useState(null);
  // # Add new state for biography
  const [biography, setBiography] = useState(userProfile?.profile?.biography || '');
  const [showExpandedPhoto, setShowExpandedPhoto] = useState(false);
  // # Add new states for tab management
  const [tabLoading, setTabLoading] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabsContainerRef = useRef(null);
  const tabsRef = useRef(null);
  // # Add dark mode state
  const [isDarkMode, setIsDarkMode] = useState(false);
  // # Add WebID share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  // # Add explicit flag to track if user is the profile owner
  const [isProfileOwner, setIsProfileOwner] = useState(!viewUsername);

  // # Available cover photos
  const coverPhotos = ['cover1.jpg', 'cover2.jpg', 'cover3.jpg', 'cover4.jpg'];

  // # Determine which profile to display
  const displayProfile = viewUsername && viewedUserProfile ? viewedUserProfile : userProfile;

  // # Get profile photo URL
  const profilePhotoUrl = React.useMemo(
    () => getProfilePhotoUrl(displayProfile), 
    [displayProfile?.photoURL]
  );

  // # Add ref for tracking last profile load log
  const lastProfileLoadRef = React.useRef({
    timestamp: 0,
    logged: false
  });

  // # Handle share button click
  const handleShareClick = () => {
    setShowShareModal(true);
  };

  // # Close share modal
  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // # Fetch viewed user data when viewing another user's profile
  useEffect(() => {
    const fetchViewedUserData = async () => {
      if (!viewUsername) return;
      
      try {
        setLoading(true);
        
        // # Query users collection for the username
        const usersCollection = collection(db, 'users');
        const q = query(usersCollection, where('username', '==', viewUsername));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          const userId = querySnapshot.docs[0].id;
          
          // # Check if the viewed profile is the user's own profile
          const isOwnProfile = user?.uid === userId;
          
          if (isOwnProfile) {
            // # User is viewing their own profile via username URL
            console.log('User is viewing their own profile via username route');
            // # Just use the userProfile from auth store
            setViewedUserProfile(null);
            setViewedUserError(null);
            // # Mark as profile owner and update URL
            setIsProfileOwner(true);
            // # Update URL to the cleaner /profile route without reloading the page
            window.history.replaceState(null, '', '/profile');
            setLoading(false);
            return;
          } else {
            // # Not the owner when viewing someone else's profile
            setIsProfileOwner(false);
          }
          
          // # Filter sensitive data for public view
          if (!user) {
            // # Only include public data
            const publicData = {
              uid: userId,
              username: userData.username,
              firstName: userData.firstName,
              middleName: userData.middleName || '',
              lastName: userData.lastName,
              photoURL: userData.photoURL,
              cover: userData.cover,
              nationality: userData.nationality,
              university: userData.university,
              phase: userData.phase,
              gpa: userData.gpa,
              gender: userData.gender,
              created_at: userData.created_at,
              email: userData.email || '',
              phoneNumber: userData.phoneNumber || '',
              selectedCountry: userData.selectedCountry || null,
              profile: {
                biography: userData.profile?.biography || '',
                socialLinks: userData.profile?.socialLinks || {}
              }
            };
            setViewedUserProfile(publicData);
          } else {
            setViewedUserProfile({
              ...userData,
              uid: userId
            });
          }
          
          // # Set cover photo
          if (userData.cover) {
            setCurrentCover(userData.cover);
          }
          
          setViewedUserError(null);
        } else {
          setViewedUserError('User not found');
          setViewedUserProfile(null);
        }
      } catch (error) {
        console.error('Error fetching viewed user data:', error);
        setViewedUserError('Error loading user profile');
      } finally {
        setLoading(false);
      }
    };
    
    if (viewUsername) {
      fetchViewedUserData();
    }
  }, [viewUsername, user, navigate]);

  // # Add logging for profile data access
  useEffect(() => {
    if ((!userProfile && !viewedUserProfile) || loading === false) return; // Skip if already loaded or no profile
    
    const now = Date.now();
    // # Skip logging if last log was within 5 seconds
    if (now - lastProfileLoadRef.current.timestamp < 5000 && lastProfileLoadRef.current.logged) {
      return;
    }
    
    const startTime = performance.now();
    console.log('Profile Page - Data Access:', {
      operation: 'profile_data_load',
      hasProfile: true,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });
    
    // # Update last load ref
    lastProfileLoadRef.current = {
      timestamp: now,
      logged: true
    };
    
    setLoading(false);
    // Only update cover if different from current
    if (displayProfile?.cover && displayProfile.cover !== currentCover) {
      setCurrentCover(displayProfile.cover);
    }
  }, [userProfile, viewedUserProfile, currentCover, loading, displayProfile]); // Add loading to dependencies

  // Optimize cover photo change handler
  const handleCoverChange = async (cover) => {
    // # If viewing another user's profile, don't allow changing their cover
    if (!isProfileOwner) return;
    
    const startTime = performance.now();
    // Skip if same cover
    if (cover === currentCover) return;
    
    try {
      setSavingCover(true);
      setSaveMessage('Saving...');

      // Single Firestore update
      await updateDoc(doc(db, 'users', user.uid), {
        cover,
        'metadata.updatedAt': new Date().toISOString()
      });

      console.log('Profile Page - Cover Update:', {
        operation: 'cover_update',
        success: true,
        timestamp: new Date().toISOString(),
        timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });

      setCurrentCover(cover);
      setSaveMessage('Cover photo updated successfully!');
      
      setTimeout(() => {
        setShowCoverCarousel(false);
        setSaveMessage('');
      }, 2000);

    } catch (error) {
      console.error('Error updating cover photo:', error);
      setSaveMessage('Failed to update cover photo. Please try again.');
    } finally {
      setSavingCover(false);
    }
  };

  // # Handle settings navigation
  const handleEditProfile = () => {
    // # If viewing another user's profile, don't show edit button
    if (!isProfileOwner) return;
    
    // Update path based on user type
    const settingsPath = isAdmin ? '/admin/settings' : '/settings';
    navigate(settingsPath);
  };

  // # Add useEffect for admin redirect if needed
  useEffect(() => {
    if (!user) return;
    
    // # Don't redirect when explicitly viewing another user's profile
    if (viewUsername) return;
    
    // Redirect admin to admin profile if accessed from regular profile route
    if (isAdmin && window.location.pathname === '/profile') {
      navigate('/admin/profile');
    }
  }, [user, isAdmin, navigate, viewUsername]);

  // # Render cover carousel
  const renderCoverCarousel = () => (
    <div className="prfl_cover-carousel-overlay" onClick={() => setShowCoverCarousel(false)}>
      <div className="prfl_cover-carousel" onClick={(e) => e.stopPropagation()}>
        <div className="prfl_carousel-header">
          <h3 className="prfl_carousel-title">Choose Cover Photo</h3>
          <button 
            className="prfl_close-carousel"
            onClick={() => setShowCoverCarousel(false)}
          >
            <MdClose />
          </button>
        </div>
        {saveMessage && (
          <div className={`prfl_save-message ${savingCover ? 'saving' : 'success'}`}>
            {saveMessage}
          </div>
        )}
        <div className="prfl_carousel-items">
          {coverPhotos.map((cover) => (
            <img
              key={cover}
              src={require(`../../aneo_media/profile/${cover}`)}
              alt={cover}
              className={`prfl_carousel-item ${currentCover === cover ? 'selected' : ''}`}
              onClick={() => !savingCover && handleCoverChange(cover)}
            />
          ))}
        </div>
      </div>
    </div>
  );

  // Optimize the photo change handler
  const handlePhotoChange = async (file) => {
    // # If viewing another user's profile, don't allow changing their photo
    if (!isProfileOwner) return;
    
    const startTime = performance.now();
    if (!file || !user?.uid) return;

    try {
      setSavingPhoto(true);
      setPhotoMessage('Uploading photo...');

      // Compress image first
      const compressedFile = await compressImage(file);
      
      const storage = getStorage();
      const photoRef = ref(storage, `avatars/${user.uid}/profile.jpg`);

      // Batch operations for better performance
      const [photoURL] = await Promise.all([
        uploadBytes(photoRef, compressedFile).then(() => getDownloadURL(photoRef)),
        userProfile?.photoURL && !userProfile.photoURL.includes('pfp.png') 
          ? deleteObject(photoRef).catch(() => {}) 
          : Promise.resolve()
      ]);

      // Single Firestore update
      await updateDoc(doc(db, 'users', user.uid), {
        photoURL,
        'metadata.updatedAt': new Date().toISOString()
      });

      console.log('Profile Page - Photo Update:', {
        operation: 'photo_update',
        success: true,
        timestamp: new Date().toISOString(),
        timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
      });

      setPhotoMessage('Photo updated successfully!');
      
      setTimeout(() => {
        setShowPhotoCarousel(false);
        setPhotoMessage('');
      }, 2000);

    } catch (error) {
      console.error('Error updating photo:', error);
      setPhotoMessage('Failed to update photo. Please try again.');
    } finally {
      setSavingPhoto(false);
    }
  };

  // # Handle profile photo click
  const handleProfilePhotoClick = (e) => {
    e.stopPropagation();
    if (!isProfileOwner) {
      // # If viewing other's profile, just expand
      setShowExpandedPhoto(true);
    } else {
      // # If own profile, show edit modal
      setShowPhotoCarousel(true);
    }
  };

  // # Render expanded photo modal
  const renderExpandedPhoto = () => (
    <div className="prfl_photo-expand-overlay" onClick={() => setShowExpandedPhoto(false)}>
      <div className="prfl_expanded-photo-container" onClick={(e) => e.stopPropagation()}>
        <img
          src={profilePhotoUrl}
          alt="Profile"
          className="prfl_expanded-photo"
        />
        <button 
          className="prfl_expand-close-btn"
          onClick={() => setShowExpandedPhoto(false)}
        >
          <MdClose />
        </button>
        {isProfileOwner && (
          <button 
            className="prfl_expand-edit-btn"
            onClick={() => {
              setShowExpandedPhoto(false);
              setShowPhotoCarousel(true);
            }}
          >
            <MdEdit /> Edit Photo
          </button>
        )}
      </div>
    </div>
  );

  // # Update photo carousel render function
  const renderPhotoCarousel = () => (
    <div className="prfl_photo-carousel-overlay" onClick={() => setShowPhotoCarousel(false)}>
      <div className="prfl_photo-carousel" onClick={(e) => e.stopPropagation()}>
        <div className="prfl_carousel-header">
          <h3 className="prfl_carousel-title">Update Profile Photo</h3>
          <button 
            className="prfl_close-carousel"
            onClick={() => setShowPhotoCarousel(false)}
          >
            <MdClose />
          </button>
        </div>
        {photoMessage && (
          <div className={`prfl_save-message ${savingPhoto ? 'saving' : 'success'}`}>
            {photoMessage}
          </div>
        )}
        <div className="prfl_photo-preview-section">
          <img
            src={profilePhotoUrl}
            alt="Current Profile"
            className="prfl_current-photo"
          />
          <div className="prfl_photo-actions">
            <button 
              className="prfl_photo-action-btn secondary"
              onClick={() => setShowExpandedPhoto(true)}
            >
              <MdZoomIn /> View Full Size
            </button>
          </div>
        </div>
        <div className="prfl_photo-upload-container">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handlePhotoChange(e.target.files[0])}
            className="prfl_photo-input"
          />
          <div className="prfl_upload-prompt">
            <MdPhotoCamera className="prfl_upload-icon" />
            <p>Click to upload new photo</p>
          </div>
        </div>
      </div>
    </div>
  );

  // # Define tabs configuration
  const tabs = [
    { id: 'about', label: 'About', icon: MdPerson },
    { id: 'posts', label: 'Posts', icon: MdArticle },
    { id: 'questions', label: 'Questions', icon: MdQuestionAnswer },
    { id: 'friends', label: 'Friends', icon: MdPeople },
    { id: 'honors', label: 'Honors', icon: MdStars },
    { id: 'achievements', label: 'Achievements', icon: MdEmojiEvents },
    { id: 'experiences', label: 'Experiences', icon: MdWork },
    { id: 'likes', label: 'Likes', icon: MdThumbUp },
    { id: 'resume', label: 'Resume', icon: MdDescription }
  ];

  // # Handle tab change with loading state
  const handleTabChange = async (tabId, index) => {
    if (tabId === activeTab) return;
    
    setTabLoading(true);
    setActiveTab(tabId);
    setActiveTabIndex(index);
    
    // # Simulate loading for smoother transitions
    await new Promise(resolve => setTimeout(resolve, 300));
    setTabLoading(false);
  };

  // # Handle tab scroll navigation
  const scrollTabs = (direction) => {
    if (!tabsRef.current) return;
    
    const container = tabsRef.current;
    const scrollAmount = direction === 'left' ? -300 : 300;
    
    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  // # Check if scroll buttons should be visible
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  
  useEffect(() => {
    const checkScroll = () => {
      if (!tabsRef.current) return;
      const { scrollWidth, clientWidth } = tabsRef.current;
      setShowScrollButtons(scrollWidth > clientWidth);
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async (newTheme) => {
    try {
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Modify the return statement to handle public view
  if (viewUsername && viewedUserError) {
    return (
      <div className={`prfl_profile-page prfl_ux-wrapper ${isDarkMode ? 'prfl_dark-mode' : ''}`}>
        <div className="prfl_ux-background-pattern"></div>
        {user ? (
          <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        ) : (
          <Header />
        )}
        <main className="prfl_profile-content prfl_ux-content">
          <div className="prfl_profile-error">
            <h2>{viewedUserError}</h2>
            <button onClick={() => navigate('/')}>Return to Home</button>
          </div>
        </main>
        {user && <DashboardFooter isDarkMode={isDarkMode} />}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`prfl_profile-loading ${isDarkMode ? 'prfl_dark-mode' : ''}`}>
        Loading profile...
      </div>
    );
  }

  return (
    <div className={`prfl_profile-page prfl_ux-wrapper ${isDarkMode ? 'prfl_dark-mode' : ''}`}>
      <div className="prfl_ux-background-pattern"></div>
      {user ? (
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      ) : (
        <Header />
      )}
      
      <main className="prfl_profile-content prfl_ux-content">
        {/* # Box 1: Cover Photo Section */}
        <div 
          className="prfl_cover-photo-section"
          onClick={() => isProfileOwner && setShowCoverCarousel(true)}
          style={{ cursor: isProfileOwner ? 'pointer' : 'default' }}
        >
          <img 
            src={require(`../../aneo_media/profile/${currentCover}`)}
            alt="Cover"
            className="prfl_cover-photo"
          />
          {isProfileOwner && (
            <div className="prfl_cover-overlay">
              <MdPhotoCamera className="prfl_change-cover-icon" />
            </div>
          )}
        </div>

        {/* # Box 2: Personal Data Section */}
        <div className="prfl_personal-data-section">
          {/* # Action Buttons */}
          <div className="prfl_profile-actions top-right">
            {!isProfileOwner && (
              <>
                <button className="prfl_action-button icon-only" title="Connect">
                  <MdPersonAdd />
                </button>
                <button className="prfl_action-button icon-only" title="Send Message">
                  <MdMessage />
                </button>
              </>
            )}
            <button 
              className="prfl_action-button icon-only" 
              title="Share Profile"
              onClick={handleShareClick}
            >
              <MdShare />
            </button>
            {isProfileOwner && (
              <button 
                className="prfl_action-button icon-only"
                onClick={handleEditProfile}
                title="Edit Profile"
              >
                <MdEdit />
              </button>
            )}
          </div>

          {/* # Profile Photo */}
          <div 
            className="prfl_profile-photo-wrapper"
            onClick={handleProfilePhotoClick}
            style={{ cursor: 'pointer' }}
          >
            <img 
              src={profilePhotoUrl}
              alt="Profile"
              className="prfl_profile-photo"
            />
            {isProfileOwner && (
              <div className="prfl_photo-overlay">
                <MdPhotoCamera className="prfl_change-photo-icon" />
              </div>
            )}
          </div>

          {/* # Profile Info */}
          <div className="prfl_profile-info">
            <div className="prfl_name-section">
              <h1 className="prfl_profile-name">
                {displayProfile?.firstName} {displayProfile?.middleName} {displayProfile?.lastName}
                <span className="prfl_profile-username">@{displayProfile?.username}</span>
              </h1>
            </div>

            {/* # Biography */}
            <h2 className="prfl_profile-bio">
              {displayProfile?.profile?.biography || 'No biography added yet.'}
            </h2>

            {/* # Location and Details */}
            <h3 className="prfl_profile-details">
              {capitalizeFirstLetter(displayProfile?.nationality)} | {capitalizeFirstLetter(displayProfile?.university)} | Joined {formatJoinDate(displayProfile?.created_at)}
            </h3>

            {/* # Social Links */}
            {displayProfile?.profile?.socialLinks && (
              <div className="prfl_social-links">
                {displayProfile.profile.socialLinks.facebook && (
                  <a href={displayProfile.profile.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="prfl_social-link facebook" aria-label="Facebook Profile">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                )}
                {displayProfile.profile.socialLinks.twitter && (
                  <a href={displayProfile.profile.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="prfl_social-link twitter" aria-label="X (Twitter) Profile">
                    <span className="x-icon">X</span>
                  </a>
                )}
                {displayProfile.profile.socialLinks.tiktok && (
                  <a href={displayProfile.profile.socialLinks.tiktok} target="_blank" rel="noopener noreferrer" className="prfl_social-link tiktok" aria-label="TikTok Profile">
                    <i className="fab fa-tiktok"></i>
                  </a>
                )}
                {displayProfile.profile.socialLinks.instagram && (
                  <a href={displayProfile.profile.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="prfl_social-link instagram" aria-label="Instagram Profile">
                    <i className="fab fa-instagram"></i>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* # Box 3: Tabs Section */}
        <div className="prfl_profile-tabs-container" ref={tabsContainerRef}>
          <div className="prfl_tabs-wrapper">
            {showScrollButtons && (
              <div 
                className="prfl_tabs-navigation left"
                onClick={() => scrollTabs('left')}
              >
                <MdChevronLeft />
              </div>
            )}
            
            <div className="prfl_profile-tabs" ref={tabsRef}>
              {tabs.map((tab, index) => (
                <button
                  key={tab.id}
                  className={`prfl_tab-button ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id, index)}
                >
                  <tab.icon className="prfl_tab-icon" />
                  {tab.label}
                </button>
              ))}
            </div>

            {showScrollButtons && (
              <div 
                className="prfl_tabs-navigation right"
                onClick={() => scrollTabs('right')}
              >
                <MdChevronRight />
              </div>
            )}
          </div>

          {/* # Tab Content with Loading State */}
          <div className="prfl_tab-content">
            {tabLoading ? (
              <div className="prfl_tab-loading">
                <div className="prfl_tab-loading-spinner" />
              </div>
            ) : (
              <>
                {activeTab === 'about' && <ProfileAbout displayProfile={displayProfile} />}
                {activeTab === 'posts' && <ProfilePosts />}
                {activeTab === 'questions' && <ProfileQuestions />}
                {activeTab === 'friends' && <ProfileFriends />}
                {activeTab === 'honors' && <ProfileHonors displayProfile={displayProfile} viewUsername={viewUsername} isAdmin={isAdmin} />}
                {activeTab === 'achievements' && <ProfileAchievements />}
                {activeTab === 'experiences' && <ProfileExperiences />}
                {activeTab === 'likes' && <ProfileLikes />}
                {activeTab === 'resume' && <ProfileResume />}
              </>
            )}
          </div>
        </div>

        {isProfileOwner && showCoverCarousel && renderCoverCarousel()}
        {isProfileOwner && showPhotoCarousel && renderPhotoCarousel()}
        {showExpandedPhoto && renderExpandedPhoto()}
        
        {/* # Web-ID Share Modal */}
        {showShareModal && (
          <WebIdShare 
            username={viewUsername || displayProfile?.username}
            isOwner={isProfileOwner}
            onClose={handleCloseShareModal} 
          />
        )}
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default Profile;
